import { useState, useRef, useCallback, useEffect } from 'react';

// Browser-native speech-to-text. Costs nothing — no transcription vendor, no
// per-minute billing. Chrome, Edge, and Safari support it; Firefox does not,
// which is why every caller must keep the textarea usable as a fallback.
//
// Caveat worth knowing: Chrome streams the audio to Google to transcribe, so
// this needs a connection. A tech deep in a steel building will get nothing
// back. That's the reason dictation fills a text field the tech can still type
// into and submit, rather than being its own submit path.

function getRecognition() {
  const Ctor = window.SpeechRecognition || window.webkitSpeechRecognition;
  return Ctor ? new Ctor() : null;
}

export const dictationSupported = () =>
  Boolean(window.SpeechRecognition || window.webkitSpeechRecognition);

export default function useDictation({ onResult } = {}) {
  const [listening, setListening] = useState(false);
  const [interim, setInterim]     = useState('');
  const [error, setError]         = useState(null);
  const recogRef = useRef(null);
  const onResultRef = useRef(onResult);

  // Keep the callback fresh without tearing down an in-flight recognition.
  useEffect(() => { onResultRef.current = onResult; }, [onResult]);

  const stop = useCallback(() => {
    recogRef.current?.stop();
    recogRef.current = null;
    setListening(false);
    setInterim('');
  }, []);

  const start = useCallback(() => {
    if (recogRef.current) return;
    const recog = getRecognition();
    if (!recog) { setError('unsupported'); return; }

    recog.continuous     = true;   // a tech describing a repair pauses to think
    recog.interimResults = true;   // show words as they land so it feels alive
    recog.lang           = 'en-US';

    recog.onresult = e => {
      let finalChunk = '';
      let pending    = '';
      for (let i = e.resultIndex; i < e.results.length; i++) {
        const t = e.results[i][0].transcript;
        if (e.results[i].isFinal) finalChunk += t;
        else pending += t;
      }
      setInterim(pending);
      if (finalChunk) onResultRef.current?.(finalChunk.trim());
    };

    recog.onerror = e => {
      // "no-speech" and "aborted" are normal (tech paused, or hit stop) — a red
      // error for those would just be noise.
      if (e.error === 'no-speech' || e.error === 'aborted') return;
      setError(e.error === 'not-allowed' ? 'denied' : 'failed');
      setListening(false);
      recogRef.current = null;
    };

    recog.onend = () => {
      setListening(false);
      setInterim('');
      recogRef.current = null;
    };

    setError(null);
    recogRef.current = recog;
    recog.start();
    setListening(true);
  }, []);

  const toggle = useCallback(() => {
    if (recogRef.current) stop();
    else start();
  }, [start, stop]);

  // Never leave the mic hot after the component goes away.
  useEffect(() => () => { recogRef.current?.abort?.(); recogRef.current = null; }, []);

  return { listening, interim, error, start, stop, toggle };
}
